import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { applySeo, getSiteUrl, type SeoInput } from '@/lib/seo';
import { injectJsonLd, removeJsonLd, FAVICON_URL } from '@/lib/jsonld';
import { ARTICLES } from '@/pages/articles/data';

const LANG_LIST = ['en', 'pt', 'de'] as const;
type Lang = (typeof LANG_LIST)[number];

const langCode = (lang: string): string => (lang === 'pt' ? 'pt-BR' : lang);

const stripSiteSuffix = (title: string): string =>
  title.replace(/\s*\|\s*cssbuybr\.wtf\s*$/i, '').trim();

const HOME_SEO: SeoInput = {
  title: 'CSSBuy Spreadsheet — Find, Compare & Research Products | cssbuybr.wtf',
  description:
    'CSSBuy Spreadsheet guide — find, compare and research products. Independent reviews, shipping costs, warehouse info, QC guides, coupons and safety tips.',
  keywords:
    'CSSBuy spreadsheet, CSSBuy review, CSSBuy shipping cost, CSSBuy coupon, CSSBuy guide, CSSBuy warehouse, CSSBuy QC, CSSBuy legit',
  ogType: 'website',
};

const PRODUCTS_META: Record<Lang, { title: string; description: string; keywords: string }> = {
  en: {
    title: 'CSSBuy Products — Find, Compare & Buy | cssbuybr.wtf',
    description:
      'Browse the full CSSBuy products collection. Filter by Shoes, Hoodies, T-Shirts, Jackets, Pants, Bags, Accessories and more, then buy through the spreadsheet.',
    keywords: 'CSSBuy products, CSSBuy spreadsheet, CSSBuy shoes, CSSBuy hoodies, CSSBuy clothing',
  },
  pt: {
    title: 'CSSBuy Produtos — Encontre, Compare e Compre | cssbuybr.wtf',
    description:
      'Navegue pela coleção completa de produtos CSSBuy. Filtre por Shoes, Hoodies, T-Shirts, Jackets, Pants, Bags, Acessórios e muito mais.',
    keywords: 'CSSBuy produtos, CSSBuy spreadsheet, CSSBuy tênis, CSSBuy moletom, CSSBuy roupas',
  },
  de: {
    title: 'CSSBuy Produkte — Finden, Vergleichen & Kaufen | cssbuybr.wtf',
    description:
      'Durchsuchen Sie die komplette CSSBuy Produktkollektion. Filtern Sie nach Shoes, Hoodies, T-Shirts, Jackets, Pants, Bags, Accessoires und mehr.',
    keywords: 'CSSBuy Produkte, CSSBuy Spreadsheet, CSSBuy Schuhe, CSSBuy Hoodies, CSSBuy Kleidung',
  },
};

const GUIDE_META: Record<Lang, { title: string; description: string }> = {
  en: {
    title: 'CSSBuy Spreadsheet Complete Guide 2026 — How to Use, Links & Tips | cssbuybr.wtf',
    description:
      'Complete CSSBuy Spreadsheet guide 2026. Learn how to use it, find links, research products, check QC, and understand shipping costs.',
  },
  pt: {
    title: 'CSSBuy Spreadsheet Guia Completo 2026 — Como Usar, Links e Dicas | cssbuybr.wtf',
    description:
      'Guia completo do CSSBuy Spreadsheet 2026. Aprenda como usar, encontrar links, pesquisar produtos, verificar QC e entender custos de envio.',
  },
  de: {
    title: 'CSSBuy Spreadsheet Vollständiger Leitfaden 2026 — Nutzung, Links & Tipps | cssbuybr.wtf',
    description:
      'Umfassender Leitfaden zum CSSBuy Spreadsheet 2026. Lernen Sie, wie Sie es verwenden, Links finden, Produkte recherchieren, QC prüfen und Versandkosten verstehen.',
  },
};

type RouteInfo =
  | { type: 'home' }
  | { type: 'products'; lang: Lang }
  | { type: 'guide'; lang: Lang }
  | { type: 'article'; lang: Lang; slug: string }
  | { type: 'unknown' };

const isLang = (s: string): s is Lang => (LANG_LIST as readonly string[]).includes(s);

function parseRoute(pathname: string): RouteInfo {
  const segments = pathname.split('/').filter(Boolean);

  if (pathname === '/') return { type: 'home' };
  if (segments.length === 1 && isLang(segments[0])) return { type: 'home' };

  if (segments[0] === 'products') return { type: 'products', lang: 'en' };
  if (segments.length === 2 && isLang(segments[0]) && segments[1] === 'products') {
    return { type: 'products', lang: segments[0] };
  }

  if (segments.length === 3 && isLang(segments[0]) && segments[1] === 'articles') {
    if (segments[2] === 'cssbuy-spreadsheet-guide') return { type: 'guide', lang: segments[0] };
    return { type: 'article', lang: segments[0], slug: segments[2] };
  }

  return { type: 'unknown' };
}

function resolveSeo(route: RouteInfo): SeoInput | null {
  const siteUrl = getSiteUrl();

  if (route.type === 'home') {
    return { ...HOME_SEO, canonical: siteUrl };
  }

  if (route.type === 'products') {
    const meta = PRODUCTS_META[route.lang];
    return {
      title: meta.title,
      description: meta.description,
      keywords: meta.keywords,
      canonical: `${siteUrl}/${route.lang}/products`,
      ogType: 'website',
    };
  }

  if (route.type === 'guide') {
    const meta = GUIDE_META[route.lang];
    return {
      title: meta.title,
      description: meta.description,
      canonical: `${siteUrl}/${route.lang}/articles/cssbuy-spreadsheet-guide`,
      ogType: 'article',
    };
  }

  if (route.type === 'article') {
    const content = ARTICLES[route.slug]?.[route.lang];
    if (!content) return null;
    return {
      title: content.metaTitle,
      description: content.metaDesc,
      canonical: `${siteUrl}/${route.lang}/articles/${route.slug}`,
      ogType: 'article',
    };
  }

  return null;
}

function buildArticle(opts: {
  headline: string;
  description: string;
  canonical: string;
  lang: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: opts.headline,
    description: opts.description,
    author: { '@type': 'Organization', name: 'CSSBuy Spreadsheet Guide' },
    publisher: {
      '@type': 'Organization',
      name: 'CSSBuy Spreadsheet Guide',
      logo: { '@type': 'ImageObject', url: FAVICON_URL },
    },
    datePublished: '2026-01-15',
    dateModified: '2026-08-26',
    mainEntityOfPage: opts.canonical,
    inLanguage: langCode(opts.lang),
  };
}

function buildBreadcrumb(items: { name: string; url: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

function resolveJsonLd(route: RouteInfo): Array<{ id: string; data: Record<string, unknown> }> {
  const siteUrl = getSiteUrl();

  if (route.type === 'home') {
    return [
      {
        id: 'jsonld-organization',
        data: {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'CSSBuy Spreadsheet Guide',
          url: siteUrl,
          logo: { '@type': 'ImageObject', url: FAVICON_URL },
        },
      },
      {
        id: 'jsonld-website',
        data: {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'CSSBuy Spreadsheet Guide',
          url: siteUrl,
          inLanguage: ['en', 'pt-BR', 'de'],
          publisher: { '@type': 'Organization', name: 'CSSBuy Spreadsheet Guide', url: siteUrl },
        },
      },
    ];
  }

  if (route.type === 'products') {
    const meta = PRODUCTS_META[route.lang];
    const canonical = `${siteUrl}/${route.lang}/products`;
    return [
      {
        id: 'jsonld-collection',
        data: {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: stripSiteSuffix(meta.title),
          description: meta.description,
          url: canonical,
          inLanguage: langCode(route.lang),
        },
      },
      {
        id: 'jsonld-breadcrumb',
        data: buildBreadcrumb([
          { name: 'Home', url: siteUrl },
          { name: 'Products', url: canonical },
        ]),
      },
    ];
  }

  if (route.type === 'guide') {
    const meta = GUIDE_META[route.lang];
    const canonical = `${siteUrl}/${route.lang}/articles/cssbuy-spreadsheet-guide`;
    return [
      {
        id: 'jsonld-article',
        data: buildArticle({
          headline: stripSiteSuffix(meta.title),
          description: meta.description,
          canonical,
          lang: route.lang,
        }),
      },
      {
        id: 'jsonld-breadcrumb',
        data: buildBreadcrumb([
          { name: 'Home', url: siteUrl },
          { name: 'CSSBuy Spreadsheet Guide', url: canonical },
        ]),
      },
    ];
  }

  if (route.type === 'article') {
    const content = ARTICLES[route.slug]?.[route.lang];
    if (!content) return [];
    const canonical = `${siteUrl}/${route.lang}/articles/${route.slug}`;
    return [
      {
        id: 'jsonld-article',
        data: buildArticle({
          headline: content.h1,
          description: content.metaDesc,
          canonical,
          lang: route.lang,
        }),
      },
      {
        id: 'jsonld-breadcrumb',
        data: buildBreadcrumb([
          { name: 'Home', url: siteUrl },
          { name: content.cardTitle, url: canonical },
        ]),
      },
    ];
  }

  return [];
}

const JSONLD_IDS = [
  'jsonld-organization',
  'jsonld-website',
  'jsonld-collection',
  'jsonld-article',
  'jsonld-breadcrumb',
];

export default function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const seo = resolveSeo(parseRoute(location.pathname));
    if (seo) applySeo(seo);
  }, [location.pathname]);

  useEffect(() => {
    JSONLD_IDS.forEach(removeJsonLd);
    const entries = resolveJsonLd(parseRoute(location.pathname));
    entries.forEach(({ id, data }) => injectJsonLd(id, data));
  }, [location.pathname]);

  return null;
}